import React from 'react';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { FIELD_TYPES } from '../../../components/ui/Form/builder/formFieldTypes';
import {
  LICENSE_AI_CREDITS_OPTIONS,
  LICENSE_DURATION_OPTIONS,
  LICENSE_STATUS_OPTIONS,
  LICENSE_USER_OPTIONS,
} from '../utils/licenseConstants';

function buildEntitlementsDescription(features = []) {
  const usersFeature = features.find((feature) => feature.key === 'users');
  const aiCreditsFeature = features.find((feature) => feature.key === 'ai_credits');

  if (!usersFeature && !aiCreditsFeature) {
    return 'Configure user seats and AI credit limits for this plan.';
  }

  return [
    usersFeature ? `${usersFeature.name} seat limits` : null,
    aiCreditsFeature
      ? `${aiCreditsFeature.name} (${aiCreditsFeature.resetPeriod || 'monthly'} reset)`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Section + field config for License create/edit — icons and copy live here;
 * custom widgets (preset select, profile search) render via FormBuilder renderField.
 */
export function getLicenseFormSections(features = []) {
  return [
    {
      id: 'identity',
      title: 'Plan identity',
      description: 'Internal name, customer-facing alias, and availability status.',
      variant: 'card',
      icon: <BadgeOutlinedIcon fontSize="small" />,
      fields: [
        {
          name: 'name',
          label: 'License Name',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g. Real Estate Gold License',
          hint: 'Internal name shown in admin lists and reports.',
          grid: { xs: 12, md: 6 },
        },
        {
          name: 'alias',
          label: 'Alias',
          type: FIELD_TYPES.TEXT,
          required: true,
          placeholder: 'e.g. Gold',
          hint: 'Short customer-facing code used in billing and UI.',
          grid: { xs: 12, md: 6 },
        },
        {
          name: 'description',
          label: 'Description',
          type: FIELD_TYPES.TEXTAREA,
          placeholder: 'Brief description of plan scope and included access',
          hint: 'Optional summary — max 300 characters.',
          minRows: 3,
          grid: { xs: 12, md: 6 },
        },
        {
          name: 'status',
          label: 'Status',
          type: FIELD_TYPES.SELECT,
          options: LICENSE_STATUS_OPTIONS,
          hint: 'Inactive plans cannot be assigned to new organisations.',
          grid: { xs: 12, md: 6 },
        },
      ],
    },
    {
      id: 'pricing',
      title: 'Pricing & billing',
      description: 'Set the plan price, billing period, and optional trial.',
      variant: 'card',
      icon: <PaymentsOutlinedIcon fontSize="small" />,
      fields: [
        {
          name: 'price',
          label: 'Plan Price',
          type: FIELD_TYPES.NUMBER,
          required: true,
          inputProps: { min: 0 },
          hint: 'Base recurring price in INR before tax.',
          grid: { xs: 12, md: 4 },
        },
        {
          name: 'durationMonths',
          label: 'Duration',
          type: FIELD_TYPES.CUSTOM,
          required: true,
          grid: { xs: 12, md: 4 },
          hint: 'Billing cycle length in months.',
          presetOptions: LICENSE_DURATION_OPTIONS,
          placeholder: 'Select duration...',
          customInputLabel: 'Enter duration (months)',
          customHelperText: 'Minimum 1 month',
          formatCustomLabel: (months) => `${Number(months).toLocaleString('en-IN')} Months`,
        },
        {
          name: 'trialDays',
          label: 'Trial Days',
          type: FIELD_TYPES.NUMBER,
          inputProps: { min: 0 },
          hint: 'Free trial before the first charge. Use 0 for no trial.',
          grid: { xs: 12, md: 4 },
        },
      ],
    },
    {
      id: 'permissions',
      title: 'Access & permissions',
      description: 'Choose the standard RBAC profile applied when this plan is assigned.',
      variant: 'card',
      icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
      fields: [
        {
          name: 'permissionProfileId',
          label: 'Permission Profile',
          type: FIELD_TYPES.CUSTOM,
          required: true,
          grid: { xs: 12 },
          hint: 'Standard RBAC template applied when this plan is assigned.',
          noOptionsText: 'No standard profiles available',
          placeholder: 'Search permission profiles...',
        },
      ],
    },
    {
      id: 'entitlements',
      title: 'Entitlements',
      description: buildEntitlementsDescription(features),
      variant: 'card',
      icon: <TuneOutlinedIcon fontSize="small" />,
      fields: [
        {
          name: 'maxUsers',
          label: 'Max Users',
          type: FIELD_TYPES.CUSTOM,
          required: true,
          grid: { xs: 12, md: 6 },
          hint: 'Maximum user seats included in this plan.',
          presetOptions: LICENSE_USER_OPTIONS,
          placeholder: 'Select user limit...',
          customInputLabel: 'Enter user limit',
          customHelperText: 'Minimum 1 user',
          formatCustomLabel: (limit) => `${Number(limit).toLocaleString('en-IN')} Users`,
        },
        {
          name: 'aiCredits',
          label: 'AI Credits',
          type: FIELD_TYPES.CUSTOM,
          required: true,
          grid: { xs: 12, md: 6 },
          hint: 'Monthly AI usage allowance; resets each billing period.',
          presetOptions: LICENSE_AI_CREDITS_OPTIONS,
          placeholder: 'Select credit limit...',
          customInputLabel: 'Enter credit limit',
          customHelperText: 'Minimum 1 credit',
          formatCustomLabel: (limit) => `${Number(limit).toLocaleString('en-IN')} Credits`,
        },
      ],
    },
  ];
}

/** Visual field order for scroll-to-error (matches section layout). */
export function getLicenseFormFieldOrder(sections = []) {
  return sections.flatMap((section) =>
    (section.fields || []).map((field) => field.name).filter(Boolean)
  );
}
